import { useScrollReveal } from '@/hooks/useScrollReveal';

const services = [
  {
    id: 1,
    title: '人材育成コンサルティング',
    description:
      '組織や個人の成長を支える専門的な知見を活かし、育成方針の設計から実行支援まで、最適なソリューションをご提案します。',
    image:
      'https://readdy.ai/api/search-image?query=Elegant%20abstract%20microscopic%20particle%20formation%20visualization%20in%20warm%20cream%20and%20soft%20amber%20tones%2C%20scientific%20artistic%20rendering%20with%20gentle%20bokeh%20effect%2C%20minimal%20sophisticated%20composition%20for%20consulting%20service%20section%2C%20high-end%20technology%20company%20aesthetic%20with%20neutral%20warm%20palette&width=600&height=400&seq=axelia-service-01&orientation=landscape',
    bgColor: 'bg-[#f5f0e8]',
  },
  {
    id: 2,
    title: 'システムエンジニアリング',
    description:
      '要件定義から設計・開発・運用まで、一貫したシステム構築サービスを提供。最新技術と確かな工程管理で、高品質なシステムをお届けします。',
    image:
      'https://readdy.ai/api/search-image?query=Minimalist%20abstract%20network%20architecture%20visualization%20with%20warm%20neutral%20tones%2C%20soft%20glowing%20connection%20lines%20on%20cream%20background%2C%20elegant%20geometric%20patterns%20representing%20system%20engineering%2C%20sophisticated%20technology%20illustration%20with%20amber%20and%20charcoal%20accents&width=600&height=400&seq=axelia-service-02&orientation=landscape',
    bgColor: 'bg-[#e8f0f0]',
  },
  {
    id: 3,
    title: 'Web制作・デジタルコンテンツ',
    description:
      '戦略的な企画からデザイン・構築・コンテンツ制作まで、Webサイトの企画立案から運用までトータルでサポートいたします。',
    image:
      'https://readdy.ai/api/search-image?query=Abstract%20digital%20web%20design%20interface%20elements%20floating%20in%20warm%20neutral%20space%2C%20elegant%20UI%20components%20and%20typography%20layout%20visualization%20in%20soft%20cream%20and%20warm%20gray%20tones%2C%20artistic%20representation%20of%20creative%20web%20production%20service%2C%20minimal%20sophisticated%20aesthetic&width=600&height=400&seq=axelia-service-03&orientation=landscape',
    bgColor: 'bg-[#f0e8e8]',
  },
];

export default function ServicesSection() {
  const { ref: sectionRef, isVisible } = useScrollReveal<HTMLElement>();

  return (
    <section id="services" ref={sectionRef} className="bg-axelia-bg-warm py-20 md:py-28 lg:py-32">
      <div className="w-full px-6 md:px-10 lg:px-16">
        {/* Section Header */}
        <div className={`text-center mb-14 md:mb-20 transition-all duration-700 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-8 opacity-0'}`}>
          <span className="inline-block text-xs font-medium tracking-[0.2em] text-axelia-accent uppercase mb-4">
            Our Services
          </span>
          <h2 className="font-serif text-2xl md:text-3xl lg:text-4xl font-bold text-axelia-text leading-snug">
            技術と創造の力で、
            <br />
            <span className="text-axelia-accent">ビジネスの可能性</span>を広げる
          </h2>
        </div>

        {/* Services Grid - Asymmetrical */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-6 max-w-6xl mx-auto">
          {/* First card - large, spans full width on mobile, half on desktop */}
          <div className={`lg:row-span-2 group cursor-pointer transition-all duration-700 delay-100 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="h-full bg-white rounded-2xl overflow-hidden border border-transparent hover:border-axelia-accent/20 transition-all duration-500">
              <div className="relative h-56 md:h-72 lg:h-[320px] overflow-hidden">
                <img
                  src={services[0].image}
                  alt={services[0].title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
                <div className="absolute bottom-4 left-5 right-5">
                  <span className="inline-block px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                    Consulting
                  </span>
                </div>
              </div>
              <div className="p-6 md:p-8">
                <h3 className="font-serif text-lg md:text-xl font-bold text-axelia-text mb-3">
                  {services[0].title}
                </h3>
                <p className="text-sm text-axelia-text-secondary leading-[1.9]">
                  {services[0].description}
                </p>
              </div>
            </div>
          </div>

          {/* Second card */}
          <div className={`group cursor-pointer transition-all duration-700 delay-200 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="h-full bg-white rounded-2xl overflow-hidden border border-transparent hover:border-axelia-accent/20 transition-all duration-500 flex flex-col">
              <div className="relative h-44 md:h-52 overflow-hidden">
                <img
                  src={services[1].image}
                  alt={services[1].title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <span className="inline-block px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                    Engineering
                  </span>
                </div>
              </div>
              <div className="p-5 md:p-6 flex-1">
                <h3 className="font-serif text-base md:text-lg font-bold text-axelia-text mb-2">
                  {services[1].title}
                </h3>
                <p className="text-sm text-axelia-text-secondary leading-[1.8]">
                  {services[1].description}
                </p>
              </div>
            </div>
          </div>

          {/* Third card */}
          <div className={`group cursor-pointer transition-all duration-700 delay-300 ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
            <div className="h-full bg-white rounded-2xl overflow-hidden border border-transparent hover:border-axelia-accent/20 transition-all duration-500 flex flex-col">
              <div className="relative h-44 md:h-52 overflow-hidden">
                <img
                  src={services[2].image}
                  alt={services[2].title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <div className="absolute bottom-3 left-4">
                  <span className="inline-block px-3 py-1 bg-black/60 backdrop-blur-sm rounded-full text-xs text-white font-medium">
                    Creative
                  </span>
                </div>
              </div>
              <div className="p-5 md:p-6 flex-1">
                <h3 className="font-serif text-base md:text-lg font-bold text-axelia-text mb-2">
                  {services[2].title}
                </h3>
                <p className="text-sm text-axelia-text-secondary leading-[1.8]">
                  {services[2].description}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
